import type { EntityAdminRecord } from '@src/components/EntityAdmin/types';
import { get } from 'lodash-es';
import { useMemo } from 'react';
import { MultiSelect } from '../../../../../Form/MultiSelect/MultiSelect';
import type { InputsAndDropdownsDrawerDropdownOption, MultiSelectDropdownDrawerOption } from '../../types';
import { useInitialRenderMultiSelectSort } from './useInitialRenderMultiSelectSort';

function getControlValues<TDrawerRecord extends EntityAdminRecord>(
  form: TDrawerRecord,
  drawerOption: MultiSelectDropdownDrawerOption<TDrawerRecord>
): string[] {
  const value = get(form, drawerOption.field);
  return Array.isArray(value) && value.every((item: unknown): item is string => typeof item === 'string') ? value : [];
}

export function MultiSelectDropdownContent<TDrawerRecord extends EntityAdminRecord>({
  drawerOption,
  form,
  originalEntity,
  isEditing,
  onFormUpdate,
  inheritedValue,
}: {
  drawerOption: MultiSelectDropdownDrawerOption<TDrawerRecord>;
  form: TDrawerRecord;
  originalEntity: TDrawerRecord | undefined;
  isEditing: boolean;
  onFormUpdate: (
    field: keyof TDrawerRecord,
    value: string[],
    /**
     * Whether to suppress touching the field.
     * This is used to prevent the field from being touched when the initial render occurs.
     */
    suppressTouch?: boolean
  ) => void;
  inheritedValue: string | undefined;
}) {
  const { initialRenderSort } = useInitialRenderMultiSelectSort((sortedValue, suppressTouch = false) => {
    if (sortedValue.length === 0) {
      return;
    }
    onFormUpdate(drawerOption.field, sortedValue, suppressTouch);
  });

  const { selections, options } = useMemo(() => {
    const optionListData = getControlValues(form, drawerOption);
    const options = drawerOption.getDropdownOptions(
      form,
      isEditing
    ) as InputsAndDropdownsDrawerDropdownOption<string>[];

    const sortedOutput = initialRenderSort(options, optionListData);
    return {
      selections: sortedOutput.selections,
      options: sortedOutput.options,
    };
  }, [drawerOption, form, initialRenderSort, isEditing]);

  const placeholder = useMemo(() => {
    return inheritedValue != null
      ? (drawerOption
          .getDropdownOptions(form, isEditing)
          // If the inherited value exists in the options, show its actual label
          .find(option => option.value === inheritedValue)?.label ?? inheritedValue)
      : drawerOption.placeholder;
  }, [drawerOption, form, inheritedValue, isEditing]);

  return (
    <MultiSelect<InputsAndDropdownsDrawerDropdownOption<string>>
      selections={selections}
      options={options}
      clearable
      getLabel={option => option.label}
      getDescription={option => option.description ?? ''}
      onChange={newValue => {
        return onFormUpdate(
          drawerOption.field,
          newValue.map(option => option.value)
        );
      }}
      disabled={(drawerOption.disabledWhenEditing && isEditing) || drawerOption.getIsDisabled?.(form, originalEntity)}
      placeholder={placeholder}
      data-testid={`entity-admin-drawer-${String(drawerOption.field)}`}
    />
  );
}
